import React, { useCallback, useMemo } from 'react';
import { X } from 'lucide-react';
import type { Node } from 'reactflow';
import type { FlowFxPersistedData } from './flowFxWorkflowTypes';
import {
  defaultParallelLabels,
  flowFxOutputHints,
  newFlowFxRowId,
  slugifyFlowFxNodeName,
} from './flowFxWorkflowTypes';
import './FlowFxConfigPanel.css';
import { WorkflowConfigPanelMaximizeButton } from './WorkflowConfigPanelFrame';

type FlowFxConfigPanelProps = {
  node: Node<FlowFxPersistedData>;
  panelMaximized?: boolean;
  onTogglePanelMaximize?: () => void;
  upstreamHints: string[];
  onUpdate: (nodeId: string, patch: Partial<FlowFxPersistedData>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
};

export default function FlowFxConfigPanel({
  node,
  panelMaximized = false,
  onTogglePanelMaximize,
  upstreamHints,
  onUpdate,
  onDelete,
  onClose,
}: FlowFxConfigPanelProps) {
  const d = node.data;
  const patch = useCallback((p: Partial<FlowFxPersistedData>) => onUpdate(node.id, p), [node.id, onUpdate]);
  const outputs = useMemo(() => flowFxOutputHints(d).join('\n'), [d]);

  const stringPatchKeys: (keyof FlowFxPersistedData)[] = [
    'ifConditionExpr',
    'ifTrueLabel',
    'ifFalseLabel',
    'switchExpr',
    'loopArrayExpr',
    'loopItemVar',
    'endCustomExpr',
    'transformInputExpr',
    'transformExpr',
    'utilLogMessage',
    'commentTitle',
    'commentBody',
  ];

  const insertToken = (token: string) => {
    const el = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
    if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
    const pk = el.getAttribute('data-fx-key') as keyof FlowFxPersistedData | null;
    if (!pk || !stringPatchKeys.includes(pk)) return;
    const cur = String((d as Record<string, unknown>)[pk as string] ?? '');
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    patch({ [pk]: next } as Partial<FlowFxPersistedData>);
    queueMicrotask(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const kind = d.kind;
  const tag = d.categoryTag === 'flow' ? 'FlowCtrl' : 'Utility';

  let body: React.ReactNode = null;

  if (kind === 'if-else') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Condition</div>
          <textarea
            data-fx-key="ifConditionExpr"
            className="workflow-fx-panel__textarea"
            value={d.ifConditionExpr}
            onChange={(e) => patch({ ifConditionExpr: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">TRUE path label</label>
          <input
            data-fx-key="ifTrueLabel"
            className="workflow-fx-panel__input"
            value={d.ifTrueLabel}
            onChange={(e) => patch({ ifTrueLabel: e.target.value })}
          />
          <label className="workflow-fx-panel__label">FALSE path label</label>
          <input
            data-fx-key="ifFalseLabel"
            className="workflow-fx-panel__input"
            value={d.ifFalseLabel}
            onChange={(e) => patch({ ifFalseLabel: e.target.value })}
          />
        </div>
      </>
    );
  } else if (kind === 'switch') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Switch expression</div>
          <input
            data-fx-key="switchExpr"
            className="workflow-fx-panel__input"
            value={d.switchExpr}
            onChange={(e) => patch({ switchExpr: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Cases</div>
          {d.switchCases.map((c) => (
            <div key={c.id} className="workflow-fx-panel__row">
              <input
                className="workflow-fx-panel__input"
                placeholder="Match"
                value={c.match}
                onChange={(e) =>
                  patch({
                    switchCases: d.switchCases.map((x) => (x.id === c.id ? { ...x, match: e.target.value } : x)),
                  })
                }
              />
              <input
                className="workflow-fx-panel__input"
                placeholder="Label"
                value={c.label}
                onChange={(e) =>
                  patch({
                    switchCases: d.switchCases.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)),
                  })
                }
              />
              <button
                type="button"
                className="workflow-fx-panel__btn"
                onClick={() => patch({ switchCases: d.switchCases.filter((x) => x.id !== c.id) })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="workflow-fx-panel__btn"
            onClick={() =>
              patch({
                switchCases: [...d.switchCases, { id: newFlowFxRowId(), match: '', label: 'New case' }],
              })
            }
          >
            + Add case
          </button>
        </div>
      </>
    );
  } else if (kind === 'loop') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Array expression</label>
          <input
            data-fx-key="loopArrayExpr"
            className="workflow-fx-panel__input"
            value={d.loopArrayExpr}
            onChange={(e) => patch({ loopArrayExpr: e.target.value })}
          />
          <label className="workflow-fx-panel__label">Item variable name</label>
          <input
            data-fx-key="loopItemVar"
            className="workflow-fx-panel__input"
            value={d.loopItemVar}
            onChange={(e) => patch({ loopItemVar: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">
            <input
              type="checkbox"
              checked={d.loopContinueOnError}
              onChange={(e) => patch({ loopContinueOnError: e.target.checked })}
            />{' '}
            Continue on item error
          </label>
          <label className="workflow-fx-panel__label">
            <input
              type="checkbox"
              checked={d.loopParallel}
              onChange={(e) => patch({ loopParallel: e.target.checked })}
            />{' '}
            Process in parallel
          </label>
          <label className="workflow-fx-panel__label">Max parallel</label>
          <input
            className="workflow-fx-panel__input"
            value={d.loopMaxParallel}
            onChange={(e) => patch({ loopMaxParallel: e.target.value })}
          />
        </div>
        <p className="workflow-fx-panel__hint">
          In-loop: {'{{ loop.item }}'}, {'{{ loop.index }}'}, {'{{ loop.isFirst }}'}, {'{{ loop.isLast }}'},{' '}
          {'{{ loop.total }}'}
        </p>
      </>
    );
  } else if (kind === 'parallel') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Branches</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {([2, 3, 4, 5] as const).map((n) => (
              <button
                key={n}
                type="button"
                className={`workflow-fx-panel__btn${d.parallelBranchCount === n ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() =>
                  patch({
                    parallelBranchCount: n,
                    parallelLabels: defaultParallelLabels(n),
                  })
                }
              >
                {n}
              </button>
            ))}
          </div>
          {d.parallelLabels.slice(0, d.parallelBranchCount).map((label, i) => (
            <div key={i} className="workflow-fx-panel__row">
              <span style={{ width: 24, flexShrink: 0 }}>{i + 1}.</span>
              <input
                className="workflow-fx-panel__input"
                value={label}
                onChange={(e) => {
                  const next = [...d.parallelLabels];
                  next[i] = e.target.value;
                  patch({ parallelLabels: next });
                }}
              />
            </div>
          ))}
        </div>
        <p className="workflow-fx-panel__hint">Connect each output to a path; use Merge to rejoin.</p>
      </>
    );
  } else if (kind === 'merge') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Inputs (top handles)</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {([2, 3, 4, 5] as const).map((n) => (
              <button
                key={n}
                type="button"
                className={`workflow-fx-panel__btn${d.mergeInputCount === n ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() => patch({ mergeInputCount: n })}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Strategy</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {(['all', 'any', 'n-of-m'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`workflow-fx-panel__btn${d.mergeStrategy === s ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() => patch({ mergeStrategy: s })}
              >
                {s === 'n-of-m' ? 'N of M' : s}
              </button>
            ))}
          </div>
          {d.mergeStrategy === 'n-of-m' && (
            <div className="workflow-fx-panel__row">
              <input
                className="workflow-fx-panel__input"
                placeholder="N"
                value={d.mergeN}
                onChange={(e) => patch({ mergeN: e.target.value })}
              />
              <span>of</span>
              <input
                className="workflow-fx-panel__input"
                placeholder="M"
                value={d.mergeM}
                onChange={(e) => patch({ mergeM: e.target.value })}
              />
            </div>
          )}
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Max wait (seconds)</label>
          <input
            className="workflow-fx-panel__input"
            value={d.mergeTimeoutSec}
            onChange={(e) => patch({ mergeTimeoutSec: e.target.value })}
          />
          <label className="workflow-fx-panel__label">On timeout</label>
          <select
            className="workflow-fx-panel__select"
            value={d.mergeOnTimeout}
            onChange={(e) => patch({ mergeOnTimeout: e.target.value as FlowFxPersistedData['mergeOnTimeout'] })}
          >
            <option value="continue">Continue with partial</option>
            <option value="fail">Fail workflow</option>
            <option value="defaults">Use defaults</option>
          </select>
        </div>
      </>
    );
  } else if (kind === 'end') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Return value</div>
          {(['none', 'last', 'custom'] as const).map((r) => (
            <label key={r} className="workflow-fx-panel__label">
              <input
                type="radio"
                name="endRet"
                checked={d.endReturnKind === r}
                onChange={() => patch({ endReturnKind: r })}
              />{' '}
              {r === 'none' ? 'None' : r === 'last' ? 'Last node output' : 'Custom expression'}
            </label>
          ))}
          {d.endReturnKind === 'custom' && (
            <textarea
              data-fx-key="endCustomExpr"
              className="workflow-fx-panel__textarea"
              value={d.endCustomExpr}
              onChange={(e) => patch({ endCustomExpr: e.target.value })}
            />
          )}
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Workflow status</label>
          <select
            className="workflow-fx-panel__select"
            value={d.endStatus}
            onChange={(e) => patch({ endStatus: e.target.value as FlowFxPersistedData['endStatus'] })}
          >
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </>
    );
  } else if (kind === 'error-handler') {
    body = (
      <p className="workflow-fx-panel__hint">
        Visual try/catch: connect the success path from the bottom-left handle and the error path from the bottom-right.
        Nested drop zones can be added in a later iteration.
      </p>
    );
  } else if (kind === 'set-variable') {
    body = (
      <div className="workflow-fx-panel__section">
        <div className="workflow-fx-panel__section-title">Variables</div>
        {d.setVariableRows.map((r) => (
          <div key={r.id} className="workflow-fx-panel__row">
            <input
              className="workflow-fx-panel__input"
              placeholder="name"
              value={r.name}
              onChange={(e) =>
                patch({
                  setVariableRows: d.setVariableRows.map((x) =>
                    x.id === r.id ? { ...x, name: e.target.value } : x
                  ),
                })
              }
            />
            <input
              className="workflow-fx-panel__input"
              placeholder="value"
              value={r.value}
              onChange={(e) =>
                patch({
                  setVariableRows: d.setVariableRows.map((x) =>
                    x.id === r.id ? { ...x, value: e.target.value } : x
                  ),
                })
              }
            />
            <button
              type="button"
              className="workflow-fx-panel__btn"
              onClick={() => patch({ setVariableRows: d.setVariableRows.filter((x) => x.id !== r.id) })}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="workflow-fx-panel__btn"
          onClick={() =>
            patch({
              setVariableRows: [...d.setVariableRows, { id: newFlowFxRowId(), name: '', value: '' }],
            })
          }
        >
          + Add variable
        </button>
      </div>
    );
  } else if (kind === 'transform') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Input</label>
          <input
            data-fx-key="transformInputExpr"
            className="workflow-fx-panel__input"
            value={d.transformInputExpr}
            onChange={(e) => patch({ transformInputExpr: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Transform type</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {(['map', 'filter', 'reduce', 'sort', 'pick', 'custom'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`workflow-fx-panel__btn${d.transformType === t ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() => patch({ transformType: t })}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Expression</label>
          <textarea
            data-fx-key="transformExpr"
            className="workflow-fx-panel__textarea"
            value={d.transformExpr}
            onChange={(e) => patch({ transformExpr: e.target.value })}
          />
        </div>
      </>
    );
  } else if (kind === 'logger') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Level</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {(['debug', 'info', 'warn', 'error'] as const).map((l) => (
              <button
                key={l}
                type="button"
                className={`workflow-fx-panel__btn${d.utilLogLevel === l ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() => patch({ utilLogLevel: l })}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Message</label>
          <textarea
            data-fx-key="utilLogMessage"
            className="workflow-fx-panel__textarea"
            value={d.utilLogMessage}
            onChange={(e) => patch({ utilLogMessage: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">
            <input
              type="checkbox"
              checked={d.utilLogPayload}
              onChange={(e) => patch({ utilLogPayload: e.target.checked })}
            />{' '}
            Full payload
          </label>
          <label className="workflow-fx-panel__label">
            <input
              type="checkbox"
              checked={d.utilLogTimestamp}
              onChange={(e) => patch({ utilLogTimestamp: e.target.checked })}
            />{' '}
            Timestamp
          </label>
          <label className="workflow-fx-panel__label">
            <input
              type="checkbox"
              checked={d.utilLogNodePath}
              onChange={(e) => patch({ utilLogNodePath: e.target.checked })}
            />{' '}
            Node path
          </label>
        </div>
        <p className="workflow-fx-panel__hint">Pass-through: output mirrors input at runtime.</p>
      </>
    );
  } else if (kind === 'comment') {
    body = (
      <>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Title</label>
          <input
            data-fx-key="commentTitle"
            className="workflow-fx-panel__input"
            value={d.commentTitle}
            onChange={(e) => {
              const v = e.target.value;
              patch({ commentTitle: v, label: v || 'Comment' });
            }}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <label className="workflow-fx-panel__label">Description</label>
          <textarea
            data-fx-key="commentBody"
            className="workflow-fx-panel__textarea"
            value={d.commentBody}
            onChange={(e) => patch({ commentBody: e.target.value })}
          />
        </div>
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Accent</div>
          <div className="workflow-fx-panel__row" style={{ flexWrap: 'wrap' }}>
            {(['gray', 'blue', 'green', 'amber', 'pink'] as const).map((a) => (
              <button
                key={a}
                type="button"
                className={`workflow-fx-panel__btn${d.commentAccent === a ? ' workflow-fx-panel__btn--on' : ''}`}
                onClick={() => patch({ commentAccent: a })}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
        <p className="workflow-fx-panel__hint">Comments are not executed and have no connections.</p>
      </>
    );
  }

  return (
    <aside className="workflow-fx-panel">
      <div className="workflow-fx-panel__head">
        <div>
          <h2 className="workflow-fx-panel__title">{d.label}</h2>
        </div>
        <span className="workflow-fx-panel__badge">{tag}</span>
        <div className="workflow-fx-panel__head-actions">
          {onTogglePanelMaximize ? (
            <WorkflowConfigPanelMaximizeButton expanded={panelMaximized} onToggle={onTogglePanelMaximize} />
          ) : null}
          <button type="button" className="workflow-fx-panel__close" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="workflow-fx-panel__section">
        <label className="workflow-fx-panel__label">Node name</label>
        <input
          className="workflow-fx-panel__input"
          value={d.nodeName}
          onChange={(e) => patch({ nodeName: slugifyFlowFxNodeName(e.target.value) })}
        />
      </div>

      {upstreamHints.length > 0 && kind !== 'comment' && (
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Variables</div>
          <div className="workflow-fx-panel__chips">
            {upstreamHints.slice(0, 20).map((h) => (
              <button key={h} type="button" className="workflow-fx-panel__chip" onClick={() => insertToken(h)}>
                {h.length > 22 ? `${h.slice(0, 20)}…` : h}
              </button>
            ))}
          </div>
        </div>
      )}

      {body}

      {outputs.trim().length > 0 && (
        <div className="workflow-fx-panel__section">
          <div className="workflow-fx-panel__section-title">Outputs</div>
          <pre className="workflow-fx-panel__outputs">{outputs}</pre>
        </div>
      )}

      <div className="workflow-fx-panel__footer">
        <button type="button" className="workflow-fx-panel__btn-danger" onClick={() => onDelete(node.id)}>
          Delete node
        </button>
      </div>
    </aside>
  );
}
